import { Injectable, inject } from '@angular/core';
import { environment } from '../../environments/environment';
import { HttpClient, HttpContext } from '@angular/common/http';
import { firstValueFrom, forkJoin, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { AllergyIntolerance, Bundle, BundleEntry, Condition, Dosage, Medication, MedicationDispense, MedicationRequest, Organization, Patient, Practitioner, Quantity, Resource, ServiceRequest } from 'fhir/r4';
import { AuthService } from './auth/auth.service';
import { PdfService } from './pdf.service';
import { ReferenceDisplayService } from './reference-display.service';
import { UtilityService } from './utility.service';
import { authBackendEndPointToken, backendEndPointToken, terminologyCanonicalUrlBaseToken } from '../app.config';
import { LoadingUIEnabled } from '../loading.interceptor';
import { referenceMatches } from './fhir-reference.util';

type MedicationRequestContext = {
    medicationRequest: MedicationRequest | null;
    patient: Patient | null;
    requester: Practitioner | null;
    performer: Resource | null;
    medication: Medication | null;
    organization: Organization | null;
    allergies: AllergyIntolerance[];
    reasonResources: Resource[];
    dispenses: MedicationDispense[];
    patientHospitalId: string | null;
};

type MedicationTableRow = {
    drug: string;
    dose: string;
    route: string;
    frequency: string;
    duration: string;
    quantity: string;
};

@Injectable({ providedIn: 'root' })
export class RequestPrintService {
    private auth = inject(AuthService);
    private pdf = inject(PdfService);
    private refDisplay = inject(ReferenceDisplayService);
    private utility = inject(UtilityService);
    private http = inject(HttpClient);
    private backend = inject(backendEndPointToken);
    private authBackend = inject(authBackendEndPointToken);
    private terminologyBaseUrl = inject(terminologyCanonicalUrlBaseToken);
    private organizationTenantTagSystem = `${this.terminologyBaseUrl}/CodeSystem/organizationTenant`;
    private organizationTenantExtensionUrl = `${this.terminologyBaseUrl}/StructureDefinition/organizationTenant`;
    private legacyHospitalExtensionUrl = 'hospitalId';
    private actorOrganizationExtensionUrl = 'http://e-likita.org/fhir/StructureDefinition/actor-organization';

    async printServiceRequest(sr: ServiceRequest, options?: { title?: string }): Promise<void> {
        if (sr.id && environment.features.enableBackendPrint) {
            try {
                const url = `${this.authBackend}/api/ServiceRequests/${sr.id}/print`;
                const blob = await firstValueFrom(this.http.get(url, { responseType: 'blob', context: new HttpContext().set(LoadingUIEnabled, true) }));
                const blobUrl = URL.createObjectURL(blob);
                window.open(blobUrl, '_blank');
                return;
            } catch (error: any) {
                if (error.status !== 0 && error.status !== 504) {
                    console.error('Backend print failed', error);
                    if (error.status === 403 || error.status === 401) {
                        alert('You do not have permission to print this document.');
                        throw error;
                    }
                }
                console.warn('Backend print unreachable, falling back to local offline generation');
            }
        }

        const title = options?.title || 'Service Request';
        const hospitalId = (this.auth.user.getValue() as any)?.hospitalId || '';

        // Hydrate context to get full resources (Patient, Organization, etc.)
        const context = await this.hydrateServiceRequestContext(sr);
        const latest = context.serviceRequest || sr;

        const subjectRef = (latest.subject as any)?.reference || '';
        const requesterRef = (latest.requester as any)?.reference || '';
        const encounterRef = (latest.encounter as any)?.reference || '';
        const performerRefs = (latest.performer || []).map((p: any) => p?.reference).filter(Boolean) as string[];

        const resolved = await firstValueFrom(
            forkJoin({
                subject: subjectRef
                    ? this.refDisplay.ensure(subjectRef, { displayPresent: (latest.subject as any)?.display || null })
                    : of(''),
                subjectId: subjectRef
                    ? this.refDisplay.ensureIdentifier(subjectRef)
                    : of(''),
                requester: requesterRef
                    ? this.refDisplay.ensure(requesterRef, { displayPresent: (latest.requester as any)?.display || null })
                    : of(''),
                encounter: encounterRef
                    ? this.refDisplay.ensure(encounterRef, { displayPresent: (latest.encounter as any)?.display || null })
                    : of(''),
                performers: performerRefs.length
                    ? forkJoin(performerRefs.map((r) => this.refDisplay.ensure(r)))
                    : of([] as string[])
            }).pipe(
                map((x) => ({
                    ...x,
                    performers: (x.performers || []).filter(Boolean).join(', ')
                }))
            )
        );

        // Use hydrated resources for richer details
        let patientResource = context.patient;
        if (!patientResource && subjectRef) {
            patientResource = await this.fetchResourceByReference<Patient>(subjectRef);
        }

        const patientDetails = this.buildPatientDetails(
            patientResource,
            resolved.subject || subjectRef,
            resolved.subjectId || context.patientHospitalId
        );
        const organizationDetails = await this.buildOrganizationDetails(context.organization || null, hospitalId);
        const requesterDetails = this.buildRequesterDetails(
            context.requester,
            resolved.requester || requesterRef
        );

        const codeText = this.codeableConceptToText((latest as any)?.code);
        const categoryText = this.codeableConceptArrayToText((latest as any)?.category);

        const reasonText = this.reasonToText(latest as any);
        const noteText = this.notesToText(latest as any);
        const serviceMetaLines = this.buildServiceMetaLines(latest as any, { includeRequisition: true, explicitRequestId: this.getBusinessRequestId(latest) });
        const serviceDetails = [
            codeText,
            categoryText ? `Category: ${categoryText}` : '',
            latest.priority ? `Priority: ${this.titleCase(latest.priority)}` : '',
            ...serviceMetaLines
        ]
            .filter(Boolean)
            .join('\n');

        const requesterStack = {
            name: requesterDetails.name || resolved.requester || requesterRef,
            specialty: requesterDetails.specialty,
            role: requesterDetails.role,
            telecom: requesterDetails.telecom,
            telecomLines: requesterDetails.telecomLines,
            subtitle: requesterDetails.subtitle
        };

        const signatureBlock = this.buildSignatureBlock(
            requesterStack.name,
            (latest as any).requesterSignature || null,
            requesterDetails.subtitle
        );

        const requestId = this.getBusinessRequestId(latest);

        const docDefinition = this.buildModernRequestDocument({
            headingText: title,
            requestId: requestId,
            org: organizationDetails,
            patient: patientDetails,
            requester: requesterStack,
            request: {
                reason: reasonText || 'N/A',
                service: serviceDetails || 'N/A',
                notes: noteText || 'None'
            },
            postContent: [signatureBlock].filter(Boolean)
        });

        this.pdf.print(docDefinition);
    }

    async printMedicationRequest(mr: MedicationRequest): Promise<void> {
        const hospitalId = (this.auth.user.getValue() as any)?.hospitalId || '';
        const contexts = await this.hydrateMedicationRequests([mr]);
        const context: MedicationRequestContext = contexts[0] ?? {
            medicationRequest: mr,
            patient: null,
            requester: null,
            performer: null,
            medication: null,
            organization: null,
            allergies: [],
            reasonResources: [],
            dispenses: [],
            patientHospitalId: null
        };

        const latest = context.medicationRequest || mr;
        const subjectRef = (latest?.subject as any)?.reference || '';
        const requesterRef = (latest?.requester as any)?.reference || '';

        const resolved = await firstValueFrom(
            forkJoin({
                subject: subjectRef
                    ? this.refDisplay.ensure(subjectRef, { displayPresent: (latest?.subject as any)?.display || null })
                    : of(''),
                subjectId: subjectRef
                    ? this.refDisplay.ensureIdentifier(subjectRef)
                    : of(''),
                requester: requesterRef
                    ? this.refDisplay.ensure(requesterRef, { displayPresent: (latest?.requester as any)?.display || null })
                    : of(''),
                medRequestId: latest?.id ? this.refDisplay.ensureIdentifier(`MedicationRequest/${latest.id}`) : of('')
            })
        );

        const patientDetails = this.buildPatientDetails(
            context.patient,
            resolved.subject || subjectRef,
            resolved.subjectId || context.patientHospitalId
        );
        const organizationDetails = await this.buildOrganizationDetails(context.organization || null, hospitalId);
        const requesterDetails = this.buildRequesterDetails(
            context.requester,
            resolved.requester || requesterRef
        );

        const diagnosisText = this.buildMedicationDiagnosisText(context);
        // Always include Intent column (shows intent and a red DO NOT DISPENSE line when not active)
        const medicationRows = this.buildMedicationTableRows([context]);
        const headers = ['Drug', 'Dose', 'Intent', 'Route', 'Frequency', 'Duration', 'Quantity'];
        const widths = [80, 55, 80, 60, 65, 55, 60];
        const medicationTableBody = medicationRows.length
            ? [headers, ...medicationRows]
            : [];
        const medicationTable = medicationTableBody.length
            ? this.buildGenericTable('MEDICATIONS', widths, medicationTableBody, { headerRows: 1 })
            : null;
        const medicationTableBlock = medicationTable ? { ...medicationTable, margin: [0, 0, 0, 20] } : null;
        const instructionsText = this.gatherPatientInstructions([context]);
        const dispenseText = this.buildDispenseSectionText([latest]);
        const allergiesText = this.formatAllergies(context.allergies);
        const medicationSummary = this.summarizeMedicationContexts([context]);
        const signatureBlock = this.buildSignatureBlock(requesterDetails.name, null, requesterDetails.subtitle);

        const metaRows = this.compactRows([
            ['Prescription ID', resolved.medRequestId || latest?.id || 'N/A'],
            ['Date', this.formatDateTime((latest as any)?.authoredOn || (latest as any)?.authoredDateTime)],
            ['Diagnosis', diagnosisText],
            ['Allergies', allergiesText]
        ]);
        const metaTable = this.buildLabelValueTable('PRESCRIPTION DETAILS', metaRows);

        const docDefinition = this.buildModernRequestDocument({
            headingText: 'Outpatient Medication Prescription',
            requestId: resolved.medRequestId || latest?.id,
            org: organizationDetails,
            patient: patientDetails,
            requester: requesterDetails,
            request: {
                reason: diagnosisText,
                service: medicationSummary,
                notes: instructionsText
            },
            additionalContent: [
                metaTable,
                medicationTableBlock,
                this.buildModernSection('DISPENSE', dispenseText || 'N/A'),
            ].filter(Boolean),
            postContent: [signatureBlock].filter(Boolean),
            sectionTitles: {
                reason: 'DIAGNOSIS',
                service: 'MEDICATION SUMMARY',
                notes: 'PATIENT INSTRUCTIONS'
            }
        });

        // If the medication request is not active, add a conspicuous banner at top
        if ((latest as any)?.status && (latest as any).status !== 'active') {
            const banner = {
                stack: [
                    {
                        columns: [
                            { width: '*', text: '' },
                            {
                                width: 'auto',
                                stack: [
                                    { text: '⚠', fontSize: 18, color: 'white', margin: [0, 0, 6, 0] }
                                ],
                                margin: [8, 0, 8, 0]
                            },
                            {
                                width: '*',
                                text: 'DO NOT DISPENSE — This request is not active',
                                color: 'white',
                                bold: true,
                                fontSize: 12
                            }
                        ],
                        columnGap: 8
                    }
                ],
                margin: [0, 0, 0, 8],
                fillColor: '#b71c1c',
                color: 'white'
            };
            docDefinition.content.unshift(banner);
            // Add a diagonal watermark stamp for single non-active request
            (docDefinition as any).watermark = { text: 'DO NOT DISPENSE', color: 'red', opacity: 0.08, bold: true, italics: false, angle: -35 };
        }

        this.pdf.print(docDefinition);
    }

    /**
     * Print a quick preview PDF for given medication contexts.
     * Useful for local review without running full hydration flows.
     */
    public printPreviewForContexts(contexts: MedicationRequestContext[], title: string = 'Medication Preview'): void {
        const medicationRows = this.buildMedicationTableRows(contexts);
        const headers = ['Drug', 'Dose', 'Intent', 'Route', 'Frequency', 'Duration', 'Quantity'];
        const widths = [90, 60, 90, 60, 65, 55, 60];
        const medicationTableBody = medicationRows.length ? [headers, ...medicationRows] : [];
        const medicationTable = medicationTableBody.length ? this.buildGenericTable('MEDICATIONS', widths, medicationTableBody, { headerRows: 1 }) : null;

        const docDefinition = this.buildModernRequestDocument({
            headingText: title,
            org: null,
            patient: { name: 'Preview', gender: '', age: '', birthDate: '', hospitalId: null },
            requester: null,
            request: { reason: '', service: '', notes: '' },
            additionalContent: [medicationTable].filter(Boolean)
        });

        this.pdf.print(docDefinition);
    }

    async printMedicationRequestGroup(groupId: string, medicationRequests: MedicationRequest[]): Promise<void> {
        const id = (groupId ?? '').toString().trim();
        const rows = Array.isArray(medicationRequests) ? medicationRequests.filter(Boolean) : [];
        if (!id || !rows.length) {
            return;
        }

        const hospitalId = (this.auth.user.getValue() as any)?.hospitalId || '';
        const contexts = await this.hydrateMedicationRequests(rows);
        if (!contexts.length) {
            return;
        }

        const primary = contexts[0];
        const latest = primary.medicationRequest || rows[0];
        const subjectRef = (latest?.subject as any)?.reference || '';
        const requesterRef = (latest?.requester as any)?.reference || '';

        const resolved = await firstValueFrom(
            forkJoin({
                subject: subjectRef
                    ? this.refDisplay.ensure(subjectRef, { displayPresent: (latest?.subject as any)?.display || null })
                    : of(''),
                subjectId: subjectRef
                    ? this.refDisplay.ensureIdentifier(subjectRef)
                    : of(''),
                requester: requesterRef
                    ? this.refDisplay.ensure(requesterRef, { displayPresent: (latest?.requester as any)?.display || null })
                    : of('')
            })
        );

        const patientDetails = this.buildPatientDetails(
            primary.patient,
            resolved.subject || subjectRef,
            resolved.subjectId || primary.patientHospitalId
        );
        const organizationDetails = await this.buildOrganizationDetails(primary.organization || null, hospitalId);
        const requesterDetails = this.buildRequesterDetails(
            primary.requester,
            resolved.requester || requesterRef
        );

        const diagnosisText = this.buildCombinedMedicationDiagnosisText(contexts);
        const allergiesText = this.formatAllergies(primary.allergies);
        const medicationSummary = this.summarizeMedicationContexts(contexts);
        const medicationRows = this.buildMedicationTableRows(contexts);
        const headers = ['Drug', 'Dose', 'Intent', 'Route', 'Frequency', 'Duration', 'Quantity'];
        const widths = [80, 55, 80, 60, 65, 55, 60];
        const medicationTableBody = medicationRows.length
            ? [headers, ...medicationRows]
            : [];
        const medicationTable = medicationTableBody.length
            ? this.buildGenericTable('MEDICATIONS', widths, medicationTableBody, { headerRows: 1 })
            : null;
        const medicationTableBlock = medicationTable ? { ...medicationTable, margin: [0, 0, 0, 20] } : null;
        const requestList = contexts
            .map((ctx) => ctx.medicationRequest)
            .filter((req): req is MedicationRequest => !!req);
        const instructionsText = this.gatherPatientInstructions(contexts);
        const dispenseText = this.buildDispenseSectionText(requestList);
        const signatureBlock = this.buildSignatureBlock(requesterDetails.name, null, requesterDetails.subtitle);

        const metaRows = this.compactRows([
            ['Group Identifier', id],
            ['Total Requests', contexts.length.toString()],
            ['Date', this.formatDateTime((latest as any)?.authoredOn || (latest as any)?.authoredDateTime)],
            ['Diagnosis', diagnosisText],
            ['Allergies', allergiesText]
        ]);
        const metaTable = this.buildLabelValueTable('PRESCRIPTION DETAILS', metaRows);

        const docDefinition = this.buildModernRequestDocument({
            headingText: 'Grouped Medication Prescription',
            org: organizationDetails,
            patient: patientDetails,
            requester: requesterDetails,
            request: {
                reason: diagnosisText,
                service: medicationSummary,
                notes: instructionsText
            },
            additionalContent: [
                metaTable,
                medicationTableBlock,
                this.buildModernSection('DISPENSE', dispenseText || 'N/A'),
            ].filter(Boolean),
            postContent: [signatureBlock].filter(Boolean),
            sectionTitles: {
                reason: 'DIAGNOSIS',
                service: 'MEDICATION SUMMARY',
                notes: 'PATIENT INSTRUCTIONS'
            }
        });

        // If any request in the group is not active, show a group-level DO NOT DISPENSE banner
        const anyNotActive = contexts.some(ctx => (ctx.medicationRequest as any)?.status && (ctx.medicationRequest as any).status !== 'active');
        if (anyNotActive) {
            const banner = {
                stack: [
                    {
                        columns: [
                            { width: '*', text: '' },
                            {
                                width: 'auto',
                                stack: [
                                    { text: '⚠', fontSize: 18, color: 'white', margin: [0, 0, 6, 0] }
                                ],
                                margin: [8, 0, 8, 0]
                            },
                            {
                                width: '*',
                                text: 'DO NOT DISPENSE — One or more requests in this group are not active',
                                color: 'white',
                                bold: true,
                                fontSize: 12
                            }
                        ],
                        columnGap: 8
                    }
                ],
                margin: [0, 0, 0, 8],
                fillColor: '#b71c1c',
                color: 'white'
            };
            docDefinition.content.unshift(banner);
        }

        this.pdf.print(docDefinition);
    }

    async printLabRequestGroup(requisitionId: string, serviceRequests: ServiceRequest[]): Promise<void> {
        const id = (requisitionId ?? '').toString().trim();
        const rows = Array.isArray(serviceRequests) ? serviceRequests.filter(Boolean) : [];
        if (!id || !rows.length) return;

        const hospitalId = (this.auth.user.getValue() as any)?.hospitalId || '';

        const first = rows[0] as ServiceRequest;
        const firstContext = await this.hydrateServiceRequestContext(first);
        const baseFirst = (firstContext.serviceRequest as ServiceRequest) || first;

        const subjectRef = (baseFirst?.subject as any)?.reference || '';
        const requesterRef = (baseFirst?.requester as any)?.reference || '';
        const encounterRef = (baseFirst?.encounter as any)?.reference || '';
        const performerRefs = (Array.isArray(baseFirst?.performer) ? baseFirst.performer : baseFirst?.performer ? [baseFirst.performer] : [])
            .map((p: any) => p?.reference)
            .filter(Boolean) as string[];

        const resolvedHeader = await firstValueFrom(
            forkJoin({
                subject: subjectRef
                    ? this.refDisplay.ensure(subjectRef, { displayPresent: (baseFirst?.subject as any)?.display || null })
                    : of(''),
                requester: requesterRef
                    ? this.refDisplay.ensure(requesterRef, { displayPresent: (baseFirst?.requester as any)?.display || null })
                    : of(''),
                encounter: encounterRef
                    ? this.refDisplay.ensure(encounterRef, { displayPresent: (baseFirst?.encounter as any)?.display || null })
                    : of(''),
                performers: performerRefs.length
                    ? forkJoin(performerRefs.map((r) => this.refDisplay.ensure(r)))
                    : of([] as string[])
            }).pipe(
                map((x) => ({
                    ...x,
                    performers: (x.performers || []).filter(Boolean).join(', ')
                }))
            )
        );

        const tableBody: any[] = [
            ['Request ID', 'Test', 'Status', 'Priority', 'Authored On']
        ];

        const codeList: string[] = [];
        rows.forEach((sr) => {
            const srAny: any = sr as any;
            const codeText = this.codeableConceptToText(srAny?.code);
            const authoredOn = srAny?.authoredOn ? new Date(srAny.authoredOn).toLocaleString() : '';

            tableBody.push([
                this.getBusinessRequestId(sr),
                codeText,
                (sr.status || '').toString(),
                (sr.priority || '').toString(),
                authoredOn
            ]);
            if (codeText) {
                codeList.push(codeText);
            }
        });

        const orgDetails = await this.buildOrganizationDetails(firstContext.organization || null, hospitalId);
        const firstExtended = baseFirst as (ServiceRequest & { patientResource?: Patient | null; patientHospitalId?: string | null; requesterResource?: Practitioner | null });
        let patientResource = firstContext.patient || firstExtended?.patientResource || null;
        if (!patientResource && subjectRef) {
            patientResource = await this.fetchResourceByReference<Patient>(subjectRef);
        }
        const derivedHospitalId = firstContext.patientHospitalId
            || firstExtended?.patientHospitalId
            || this.extractHospitalIdentifierFromPatient(patientResource);
        const patientDetails = this.buildPatientDetails(
            patientResource,
            resolvedHeader.subject || subjectRef,
            derivedHospitalId || null
        );
        const requesterDetails = this.buildRequesterDetails(firstContext.requester || firstExtended?.requesterResource || null, resolvedHeader.requester || requesterRef);
        const reasonText = rows
            .map((sr) => this.reasonToText(sr as any))
            .filter(Boolean)
            .join('\n\n');
        const noteText = rows
            .map((sr) => this.notesToText(sr as any))
            .filter(Boolean)
            .join('\n\n');
        
        const serviceSummary = Array.from(new Set(codeList))
            .filter(Boolean)
            .join(', ') || `${rows.length} request(s)`;

        const requestRows = this.compactRows([
            ['Requisition', (rows[0] as any)?.requisition?.value || id],
            ['Requester', resolvedHeader.requester || requesterRef],
            ['Performer(s)', resolvedHeader.performers || performerRefs.join(', ')],
            ['Encounter', resolvedHeader.encounter || encounterRef]
        ]);
        const requestMetaTable = this.buildLabelValueTable('REQUEST DETAILS', requestRows);

        const labItemsTable = this.buildGenericTable('REQUESTED TESTS', [70, '*', 70, 60, 90], tableBody, { headerRows: 1 });
        const groupedRequestBoxes = rows
            .map((sr, idx) => this.buildGroupedRequestDetailBox(sr, idx + 1, this.getBusinessRequestId(sr)))
            .filter(Boolean);
        const additionalBlocks: any[] = [];
        if (requestMetaTable) {
            additionalBlocks.push(requestMetaTable);
        }
        if (labItemsTable) {
            additionalBlocks.push(labItemsTable);
        }
        additionalBlocks.push(...groupedRequestBoxes);

        const docDefinition = this.buildModernRequestDocument({
            headingText: 'Lab Requests (Group)',
            org: orgDetails,
            patient: patientDetails,
            requester: requesterDetails,
            request: {
                reason: reasonText || 'N/A',
                service: serviceSummary,
                notes: noteText || 'None'
            },
            additionalContent: additionalBlocks
        });

        this.pdf.print(docDefinition);
    }

    private buildPatientDetails(patient: Patient | null, fallbackDisplay: string, explicitHospitalId?: string | null) {
        const name = this.getPatientName(patient) || fallbackDisplay || '';
        const birthDate = patient?.birthDate ? new Date(patient.birthDate).toLocaleDateString() : '';
        const age = patient?.birthDate ? this.computeAge(patient.birthDate) : '';
        const reference = patient?.id ? `Patient/${patient.id}` : fallbackDisplay;
        const hospitalId = explicitHospitalId || this.extractHospitalIdentifierFromPatient(patient) || '';

        return {
            name,
            birthDate,
            age,
            gender: this.titleCase(patient?.gender || ''),
            reference,
            hospitalId
        };
    }

    private async buildOrganizationDetails(org: Organization | null, fallbackHospitalId?: string) {
        if (!org && !fallbackHospitalId) {
            return null;
        }

        const address = org?.address?.length ? this.formatAddress(org.address[0]) : '';
        const telecom = this.formatTelecom(org?.telecom);
        const logo = await this.resolveOrganizationLogo(org);
        const name = org?.name || '';
        let hospitalId = fallbackHospitalId || '';
        if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(hospitalId)) {
            hospitalId = '';
        }

        if (!name && !address && !telecom && !logo && !hospitalId) {
            return null;
        }

        return { name, address, telecom, logo, hospitalId };
    }

    private buildRequesterDetails(practitioner: Practitioner | null, fallbackName: string) {
        const name = this.getPractitionerName(practitioner) || fallbackName || '';
        const telecomLines = this.formatTelecomLines(practitioner?.telecom);
        const telecom = telecomLines.join(' | ');
        const specialty = this.codeableConceptArrayToText(practitioner?.qualification?.map((q) => q.code));

        const roleConcepts: any[] = [];
        const practitionerRoles = (practitioner as any)?.practitionerRole;
        if (Array.isArray(practitionerRoles)) {
            practitionerRoles.forEach((roleItem: any) => {
                if (Array.isArray(roleItem?.code)) {
                    roleConcepts.push(...roleItem.code);
                } else if (roleItem?.code) {
                    roleConcepts.push(roleItem.code);
                }
            });
        }
        const role = this.codeableConceptArrayToText(roleConcepts);
        const subtitle = [role, specialty].filter(Boolean).join(' · ') || null;

        return { name, specialty, role, telecom, telecomLines, subtitle };
    }

    private compactRows(rows: Array<[string, unknown]>): Array<[string, string]> {
        return rows
            .filter(([, value]) => value !== null && value !== undefined && `${value}`.trim() !== '')
            .map(([label, value]) => [label, String(value)] as [string, string]);
    }

    private buildModernRequestDocument(config: {
        headingText?: string;
        org?: { name?: string; address?: string; telecom?: string; logo?: string | null; hospitalId?: string | null } | null;
        patient: { name?: string; gender?: string; age?: string; birthDate?: string; hospitalId?: string | null };
        requester?: { name?: string; specialty?: string; role?: string; telecom?: string; telecomLines?: string[] | null; subtitle?: string | null } | null;
        request: { reason: string; service: string; notes: string };
        additionalContent?: any[];
        postContent?: any[];
        sectionTitles?: { reason?: string; service?: string; notes?: string };
        requestId?: string;
    }) {
        const { headingText, org, patient, requester, request, additionalContent = [], postContent = [], sectionTitles } = config;
        const reasonSectionTitle = sectionTitles?.reason ?? 'CLINICAL DETAILS / REASON FOR REQUEST';
        const serviceSectionTitle = sectionTitles?.service ?? 'SERVICE REQUESTED';
        const notesSectionTitle = sectionTitles?.notes ?? 'ADDITIONAL NOTES';
        const content: any[] = [];

        if (org && (org.name || org.address || org.telecom || org.logo || org.hospitalId)) {
            content.push(this.buildModernOrgHeader(org));
        }

        if (headingText) {
            content.push({ text: headingText, style: 'header', marginBottom: config.requestId ? 2 : 6 });
            if (config.requestId) {
                content.push({ text: `Request ID: ${config.requestId}`, style: 'subheader', marginBottom: 6 });
            }
        }

        content.push(this.buildModernSeparator());
        content.push(this.buildModernInfoSection(patient, requester));

        content.push(this.buildModernSection(reasonSectionTitle, request.reason || 'N/A'));
        content.push(this.buildModernSection(serviceSectionTitle, request.service || 'N/A'));

        additionalContent.filter(Boolean).forEach((block) => content.push(block));

        content.push(this.buildModernSection(notesSectionTitle, request.notes || 'None'));

        postContent.filter(Boolean).forEach((block) => content.push(block));

        content.push({ text: `Generated: ${new Date().toLocaleString()}`, style: 'footerNote', margin: [0, 24, 0, 0], alignment: 'right' });

        return {
            pageSize: 'A4',
            pageMargins: [32, 40, 32, 40],
            content,
            styles: {
                header: {
                    fontSize: 18,
                    bold: true,
                    color: '#2c3e50'
                },
                subheader: {
                    fontSize: 10,
                    color: '#7f8c8d'
                },
                label: {
                    fontSize: 8,
                    bold: true,
                    color: '#95a5a6',
                    marginBottom: 2
                },
                nameValue: {
                    fontSize: 12,
                    bold: true,
                    marginBottom: 2
                },
                smallValue: {
                    fontSize: 10,
                    marginBottom: 2
                },
                detailKey: {
                    fontSize: 11,
                    bold: true,
                    color: '#2c3e50'
                },
                detailValue: {
                    fontSize: 11,
                    color: '#5f6a6a'
                },
                sectionHeader: {
                    fontSize: 10,
                    bold: true,
                    color: '#2c3e50'
                },
                bodyText: {
                    fontSize: 11,
                    lineHeight: 1.2
                },
                tableTitle: {
                    fontSize: 10,
                    bold: true,
                    color: '#2c3e50',
                    margin: [0, 16, 0, 6]
                },
                footerNote: {
                    fontSize: 9,
                    color: '#7f8c8d'
                },
                signatureName: { fontSize: 11, bold: true },
                signatureRole: { fontSize: 10, color: '#555' }
            },
            defaultStyle: {
                font: 'Roboto',
                fontSize: 11
            }
        };
    }

    private buildModernOrgHeader(details: { name?: string; address?: string; telecom?: string; logo?: string | null; hospitalId?: string | null }) {
        const rightStack = [
            details.name ? { text: details.name, style: 'header', alignment: 'right' } : null,
            details.address ? { text: details.address, style: 'subheader', alignment: 'right', opacity: 0.6 } : null,
            details.telecom ? { text: details.telecom, style: 'subheader', alignment: 'right', opacity: 0.6 } : null,
            details.hospitalId
                ? { text: `Hospital ID: ${details.hospitalId}`, style: 'subheader', alignment: 'right', opacity: 0.6 }
                : null
        ].filter(Boolean);

        return {
            columns: [
                details.logo
                    ? { image: details.logo, width: 60, alignment: 'left' }
                    : { width: 60, text: '' },
                { stack: rightStack.length ? rightStack : [{ text: '', alignment: 'right' }] }
            ],
            marginBottom: 10
        };
    }

    private buildModernSeparator() {
        return {
            canvas: [{ type: 'line', x1: 0, y1: 5, x2: 515, y2: 5, lineWidth: 1 }],
            marginBottom: 20
        };
    }

    private buildModernInfoSection(
        patient: { name?: string; gender?: string; age?: string; birthDate?: string; hospitalId?: string | null },
        requester?: { name?: string; specialty?: string; role?: string; telecom?: string; telecomLines?: string[] | null; subtitle?: string | null } | null
    ) {
        const patientBox = {
            width: 'auto',
            table: {
                widths: [200],
                body: [
                    [
                        {
                            stack: [
                                { text: 'PATIENT DETAILS', style: 'label' },
                                { text: patient?.name || 'N/A', style: 'nameValue' },
                                { text: patient?.hospitalId ? `Patient ID: ${patient.hospitalId}` : '', style: 'smallValue' },
                                { text: patient?.gender ? `Gender: ${patient.gender}` : '', style: 'smallValue' },
                                { text: patient?.age ? `Age: ${patient.age}` : '', style: 'smallValue' },
                                { text: patient?.birthDate ? `DOB: ${patient.birthDate}` : '', style: 'smallValue' }
                            ].filter((entry) => !!entry?.text),
                            fillColor: '#f9f9f9',
                            margin: [10, 10, 10, 10]
                        }
                    ]
                ]
            },
            layout: 'lightHorizontalLines'
        };

        const requesterColumn = {
            width: '*',
            stack: [
                { text: 'REQUESTED BY', style: 'label', alignment: 'right' },
                { text: requester?.name || 'N/A', style: 'nameValue', alignment: 'right' },
                requester?.subtitle || requester?.specialty || requester?.role
                    ? { text: requester?.subtitle || requester?.specialty || requester?.role, style: 'smallValue', alignment: 'right' }
                    : null,
                requester?.telecomLines?.length
                    ? requester.telecomLines.map((line) => ({ text: line, style: 'smallValue', alignment: 'right' }))
                    : (requester?.telecom
                        ? { text: requester.telecom, style: 'smallValue', alignment: 'right' }
                        : null)
            ].filter(Boolean),
            marginLeft: 20
        };

        const requesterStackRaw = Array.isArray(requesterColumn.stack)
            ? requesterColumn.stack
            : [requesterColumn.stack];
        const requesterStack: any[] = [];
        requesterStackRaw.forEach((entry) => {
            if (Array.isArray(entry)) {
                entry.forEach((sub) => {
                    if (sub) {
                        requesterStack.push(sub);
                    }
                });
            } else if (entry) {
                requesterStack.push(entry);
            }
        });

        return {
            columns: [
                patientBox,
                {
                    ...requesterColumn,
                    stack: requesterStack
                }
            ],
            marginBottom: 30
        };
    }

    private buildModernSection(title: string, textValue: string) {
        return {
            stack: [
                {
                    text: title,
                    style: 'sectionHeader',
                    fillColor: '#eeeeee',
                    margin: [0, 0, 0, 5]
                },
                { text: textValue || 'N/A', style: 'bodyText', margin: [0, 0, 0, 20] }
            ]
        };
    }

    private buildLabelValueTable(title: string, rows: Array<[string, string]>) {
        if (!rows?.length) {
            return null;
        }

        return {
            stack: [
                { text: title, style: 'tableTitle' },
                ...rows.map(([label, value]) => ({
                    columns: [
                        { width: 150, text: label, style: 'detailKey' },
                        { width: '*', text: value, style: 'detailValue' }
                    ],
                    columnGap: 8,
                    margin: [0, 2, 0, 6]
                }))
            ]
        };
    }

    private buildGenericTable(title: string, widths: Array<number | string>, body: any[][], options?: { headerRows?: number }) {
        if (!body?.length) {
            return null;
        }

        return {
            stack: [
                { text: title, style: 'tableTitle' },
                {
                    table: {
                        headerRows: options?.headerRows ?? 0,
                        widths,
                        body
                    },
                    layout: 'lightHorizontalLines'
                }
            ]
        };
    }

    private buildServiceMetaLines(sr: ServiceRequest | null, options?: { includeRequisition?: boolean, explicitRequestId?: string }): string[] {
        if (!sr) {
            return [];
        }

        const details: string[] = [];
        const reqId = options?.explicitRequestId || sr.id;
        if (reqId) {
            details.push(`Request ID: ${reqId}`);
        }
        if (options?.includeRequisition && (sr as any)?.requisition?.value) {
            details.push(`Requisition: ${(sr as any).requisition.value}`);
        }
        const authoredOn = this.formatDateTime((sr as any)?.authoredOn || (sr as any)?.occurrenceDateTime);
        if (authoredOn) {
            details.push(`Date Requested: ${authoredOn}`);
        }
        return details;
    }

    private buildGroupedRequestDetailBox(sr: ServiceRequest | null, position: number, explicitRequestId?: string) {
        if (!sr) {
            return null;
        }

        const srAny: any = sr as any;
        const reasonText = this.reasonToText(srAny) || 'N/A';
        const categoryText = this.codeableConceptArrayToText(srAny?.category);
        const codeText = this.codeableConceptToText(srAny?.code);
        const serviceMetaLines = this.buildServiceMetaLines(sr, { includeRequisition: true, explicitRequestId });
        const serviceDetails = [
            codeText,
            categoryText ? `Category: ${categoryText}` : '',
            sr.priority ? `Priority: ${this.titleCase(sr.priority)}` : '',
            ...serviceMetaLines
        ]
            .filter(Boolean)
            .join('\n') || 'N/A';
        const notesText = this.notesToText(srAny) || 'None';

        const sectionStack = [
            { text: 'CLINICAL DETAILS / REASON FOR REQUEST', style: 'sectionHeader', fillColor: '#f5f7fb', margin: [0, 0, 0, 4] },
            { text: reasonText, style: 'bodyText', margin: [0, 0, 0, 12] },
            { text: 'SERVICE REQUESTED', style: 'sectionHeader', fillColor: '#f5f7fb', margin: [0, 0, 0, 4] },
            { text: serviceDetails, style: 'bodyText', margin: [0, 0, 0, 12] },
            { text: 'ADDITIONAL NOTES', style: 'sectionHeader', fillColor: '#f5f7fb', margin: [0, 0, 0, 4] },
            { text: notesText, style: 'bodyText' }
        ];

        const headingId = explicitRequestId || sr.id;
        const headingText = headingId ? `Request ${position} · ${headingId}` : `Request ${position}`;

        return {
            table: {
                widths: ['*'],
                body: [[
                    {
                        stack: [
                            { text: headingText, style: 'tableTitle', margin: [0, 0, 0, 6] },
                            ...sectionStack
                        ].filter(Boolean),
                        margin: [10, 12, 10, 12]
                    }
                ]]
            },
            layout: {
                hLineWidth: () => 0.6,
                vLineWidth: () => 0.6,
                hLineColor: () => '#dfe6e9',
                vLineColor: () => '#dfe6e9',
                paddingLeft: () => 0,
                paddingRight: () => 0,
                paddingTop: () => 0,
                paddingBottom: () => 0
            },
            margin: [0, 12, 0, 0]
        };
    }

    private buildSignatureBlock(name?: string, signatureValue?: string | null, subtitle?: string | null) {
        const trimmedName = (name || '').trim();
        const normalized = this.normalizeSignatureValue(signatureValue);
        if (!trimmedName && !normalized) {
            return null;
        }

        const signatureColumnWidth = 300;
        const stack: any[] = [];
        if (normalized?.image) {
            stack.push({ image: normalized.image, width: 150, height: 60, margin: [0, 0, 0, 6], alignment: 'right' });
        } else if (normalized?.text) {
            stack.push({ text: normalized.text, italics: true, margin: [0, 0, 0, 6], alignment: 'right' });
        }

        stack.push({ canvas: [{ type: 'line', x1: 0, y1: 0, x2: signatureColumnWidth, y2: 0, lineWidth: 0.5 }], margin: [0, 2, 0, 6] });

        if (trimmedName) {
            stack.push({ text: trimmedName, style: 'signatureName', alignment: 'right' });
        }
        if (subtitle) {
            stack.push({ text: subtitle, style: 'signatureRole', alignment: 'right' });
        }
        stack.push({ text: 'Signature & Stamp', style: 'signatureRole', margin: [0, 6, 0, 0], alignment: 'right' });

        return {
            columns: [
                { width: '*', text: '' },
                { width: signatureColumnWidth, stack, alignment: 'right' }
            ],
            columnGap: 0,
            margin: [0, 12, 0, 0]
        };
    }

    private normalizeSignatureValue(signature?: string | null) {
        if (!signature) {
            return null;
        }

        const trimmed = signature.trim();
        if (!trimmed) {
            return null;
        }

        if (trimmed.startsWith('data:')) {
            return { image: trimmed };
        }

        const base64Pattern = /^[A-Za-z0-9+/=]+$/;
        if (base64Pattern.test(trimmed) && trimmed.length > 32) {
            return { image: `data:image/png;base64,${trimmed}` };
        }

        return { text: trimmed };
    }

    private async fetchResourceByReference<T>(reference?: string | null): Promise<T | null> {
        const { type, id } = this.splitReference(reference);
        if (!type || !id) {
            return null;
        }
        try {
            return await firstValueFrom(
                this.http.get<T>(`${this.backend}/${type}/${id}`, {
                    context: new HttpContext().set(LoadingUIEnabled, false)
                })
            );
        } catch {
            return null;
        }
    }

    private splitReference(reference?: string | null): { type: string | null; id: string | null } {
        if (!reference) {
            return { type: null, id: null };
        }
        const parts = reference.split('/').filter(Boolean);
        if (parts.length < 2) {
            return { type: null, id: null };
        }
        const id = parts[parts.length - 1] || null;
        const type = parts[parts.length - 2] || null;
        return { type, id };
    }

    private async hydrateServiceRequestContext(sr: ServiceRequest | null): Promise<{
        serviceRequest: ServiceRequest | null;
        patient: Patient | null;
        requester: Practitioner | null;
        performer: Resource | null;
        organization: Organization | null;
        patientHospitalId: string | null;
    }> {
        const enriched = sr as ServiceRequest & {
            patientResource?: Patient | null;
            requesterResource?: Practitioner | null;
            performerResource?: Resource | null;
            organizationResource?: Organization | null;
            patientHospitalId?: string | null;
        };
        const baseline = {
            serviceRequest: sr,
            patient: enriched?.patientResource || null,
            requester: enriched?.requesterResource || null,
            performer: enriched?.performerResource || null,
            organization: enriched?.organizationResource || null,
            patientHospitalId: enriched?.patientHospitalId || null
        };

        const srId = (sr?.id ?? '').toString().trim();
        if (!srId) {
            return baseline;
        }

        const queryParts = [
            `_id=${encodeURIComponent(srId)}`,
            '_format=json',
            '_include=ServiceRequest:subject',
            '_include=ServiceRequest:requester',
            '_include=ServiceRequest:performer',
            '_revinclude=Observation:based-on'
        ];
        const url = `${this.backend}/ServiceRequest?${queryParts.join('&')}`;

        try {
            const bundle = await firstValueFrom(
                this.http.get<Bundle>(url, { context: new HttpContext().set(LoadingUIEnabled, false) })
            );
            const resourceMap = this.buildResourceMap(bundle?.entry);
            const latest = this.findResourceByReference<ServiceRequest>(resourceMap, `ServiceRequest/${srId}`) || sr;

            const patient = baseline.patient
                || this.findResourceByReference<Patient>(resourceMap, latest?.subject?.reference)
                || null;
            const requester = baseline.requester
                || this.findResourceByReference<Practitioner>(resourceMap, latest?.requester?.reference)
                || null;
            const performerRef = this.extractPerformerReference(latest);
            const performer = baseline.performer
                || (performerRef ? this.findResourceByReference<Resource>(resourceMap, performerRef) : null)
                || null;

            let organization = baseline.organization || this.resolveOrganizationResource(resourceMap);
            if (!organization) {
                const requesterOrgId = this.getOrganizationTenantId(latest);
                if (requesterOrgId) {
                    const fetchedOrg = await this.fetchOrganizationById(requesterOrgId);
                    organization = fetchedOrg || organization;
                }
            }

            const patientHospitalId = baseline.patientHospitalId || this.extractHospitalIdentifierFromPatient(patient);

            return {
                serviceRequest: latest,
                patient,
                requester,
                performer,
                organization,
                patientHospitalId
            };
        } catch {
            return baseline;
        }
    }

    private async hydrateMedicationRequests(requests: MedicationRequest[]): Promise<MedicationRequestContext[]> {
        if (!Array.isArray(requests) || !requests.length) {
            return [];
        }

        const ids = Array.from(
            new Set(
                requests
                    .map((req) => (req?.id ?? '').toString().trim())
                    .filter((value) => !!value)
            )
        );

        const resourceMap = await this.fetchMedicationRequestBundle(ids);
        const contexts: MedicationRequestContext[] = [];
        for (const request of requests) {
            contexts.push(await this.composeMedicationContext(request, resourceMap));
        }
        return contexts;
    }

    private async fetchMedicationRequestBundle(ids: string[]): Promise<Map<string, Resource> | null> {
        if (!ids.length) {
            return null;
        }

        const queryParts = [
            `_id=${ids.map((id) => encodeURIComponent(id)).join(',')}`,
            '_format=json',
            '_include=MedicationRequest:subject',
            '_include=MedicationRequest:requester',
            '_include=MedicationRequest:performer',
            '_include=MedicationRequest:medication',
            '_include=MedicationRequest:reason-reference',
            '_revinclude=MedicationDispense:prescription',
            '_revinclude=AllergyIntolerance:patient'
        ];
        const url = `${this.backend}/MedicationRequest?${queryParts.join('&')}`;

        try {
            const bundle = await firstValueFrom(
                this.http.get<Bundle>(url, {
                    context: new HttpContext().set(LoadingUIEnabled, false)
                })
            );
            return this.buildResourceMap(bundle?.entry);
        } catch (error) {
            console.warn('Failed to hydrate MedicationRequest bundle', error);
            return null;
        }
    }

    private async composeMedicationContext(
        base: MedicationRequest,
        resourceMap: Map<string, Resource> | null
    ): Promise<MedicationRequestContext> {
        const enriched = base as MedicationRequest & {
            patientResource?: Patient | null;
            requesterResource?: Practitioner | null;
            performerResource?: Resource | null;
            medicationResource?: Medication | null;
            organizationResource?: Organization | null;
            patientHospitalId?: string | null;
        };

        let organization: Organization | null = enriched.organizationResource || null;

        const id = (base?.id ?? '').toString().trim();
        const latest = resourceMap && id
            ? this.findResourceByReference<MedicationRequest>(resourceMap, `MedicationRequest/${id}`) || base
            : base;

        let patient = enriched.patientResource
            || (resourceMap ? this.findResourceByReference<Patient>(resourceMap, latest?.subject?.reference) : null)
            || null;
        if (!patient && latest?.subject?.reference) {
            patient = await this.fetchResourceByReference<Patient>(latest.subject.reference);
        }

        let requester = enriched.requesterResource
            || (resourceMap ? this.findResourceByReference<Practitioner>(resourceMap, latest?.requester?.reference) : null)
            || null;
        if (!requester && latest?.requester?.reference) {
            requester = await this.fetchResourceByReference<Practitioner>(latest.requester.reference);
        }
        if (!requester && resourceMap && latest?.requester?.reference) {
            const requesterResource = this.findResourceByReference<Resource>(resourceMap, latest.requester.reference);
            if (requesterResource?.resourceType === 'PractitionerRole') {
                const practitionerRef = (requesterResource as any)?.practitioner?.reference;
                if (practitionerRef) {
                    requester = await this.fetchResourceByReference<Practitioner>(practitionerRef);
                }
                if (!organization && (requesterResource as any)?.organization?.reference) {
                    organization = this.findResourceByReference<Organization>(
                        resourceMap,
                        (requesterResource as any)?.organization?.reference
                    ) || organization;
                }
            } else if (requesterResource?.resourceType === 'Practitioner') {
                requester = requesterResource as Practitioner;
            }
        }

        const performerRef = this.extractPerformerReference(latest as any);
        let performer = enriched.performerResource
            || (performerRef && resourceMap ? this.findResourceByReference<Resource>(resourceMap, performerRef) : null)
            || null;
        if (!performer && performerRef) {
            performer = await this.fetchResourceByReference<Resource>(performerRef);
        }

        let medication = enriched.medicationResource
            || (resourceMap ? this.findResourceByReference<Medication>(resourceMap, (latest as any)?.medicationReference?.reference) : null)
            || null;
        if (!medication && (latest as any)?.medicationReference?.reference) {
            medication = await this.fetchResourceByReference<Medication>((latest as any).medicationReference.reference);
        }

        organization = organization
            || (performer?.resourceType === 'Organization' ? (performer as Organization) : null)
            || (patient?.managingOrganization?.reference && resourceMap
                ? this.findResourceByReference<Organization>(resourceMap, patient.managingOrganization.reference)
                : null)
            || (resourceMap ? this.resolveOrganizationResource(resourceMap) : null);

        if (!organization) {
            const orgId = this.getOrganizationTenantId(latest as any);
            if (orgId) {
                organization = await this.fetchOrganizationById(orgId) || organization;
            }
        }

        const allergies = resourceMap && patient
            ? this.extractAllergiesFromMap(resourceMap, patient)
            : [];

        const reasonResources = resourceMap
            ? this.extractReasonResources(resourceMap, latest)
            : [];

        const dispenses = resourceMap
            ? this.extractDispensesFromMap(resourceMap, latest)
            : [];

        const patientHospitalId = enriched.patientHospitalId || this.extractHospitalIdentifierFromPatient(patient);

        return {
            medicationRequest: latest,
            patient,
            requester,
            performer,
            medication,
            organization,
            allergies,
            reasonResources,
            dispenses,
            patientHospitalId
        };
    }

    private extractAllergiesFromMap(map: Map<string, Resource>, patient: Patient): AllergyIntolerance[] {
        if (!patient?.id) {
            return [];
        }
        const target = `Patient/${patient.id}`;
        const matches: AllergyIntolerance[] = [];
        map.forEach((resource) => {
            if (resource?.resourceType === 'AllergyIntolerance') {
                const allergy = resource as AllergyIntolerance;
                const patientRef = allergy.patient?.reference || '';
                if (referenceMatches(patientRef, target)) {
                    matches.push(allergy);
                }
            }
        });
        return matches;
    }

    private extractReasonResources(map: Map<string, Resource>, request: MedicationRequest | null): Resource[] {
        if (!request?.reasonReference?.length) {
            return [];
        }
        const refs = request.reasonReference.map((ref: any) => ref?.reference || '').filter(Boolean);
        const matches: Resource[] = [];
        refs.forEach((ref) => {
            const resource = this.findResourceByReference<Resource>(map, ref);
            if (resource) {
                matches.push(resource);
            }
        });
        return matches;
    }

    private extractDispensesFromMap(map: Map<string, Resource>, request: MedicationRequest | null): MedicationDispense[] {
        if (!request?.id) {
            return [];
        }
        const target = `MedicationRequest/${request.id}`;
        const matches: MedicationDispense[] = [];
        map.forEach((resource) => {
            if (resource?.resourceType === 'MedicationDispense') {
                const dispense = resource as MedicationDispense;
                const authorizing = Array.isArray(dispense.authorizingPrescription)
                    ? dispense.authorizingPrescription
                    : [];
                if (authorizing.some((ref) => referenceMatches(ref?.reference || '', target))) {
                    matches.push(dispense);
                }
            }
        });
        return matches;
    }

    private buildMedicationTableRows(contexts: MedicationRequestContext[]): any[][] {
        return contexts.map((context) => {
            const row = this.buildMedicationTableRow(context);

            const intentText = (context.medicationRequest as any)?.intent || '';
            const status = (context.medicationRequest as any)?.status || '';

            // Use a compact badge-like representation that prints clearly in black & white:
            // - Active: plain intent string
            // - Non-active: two-line cell with intent and a bold UPPERCASE 'DO NOT DISPENSE' line
            // Framed badge variant (black border, white background) to print well in color and B/W.
            const intentCell: any = status !== 'active'
                ? {
                    stack: [
                        { text: (intentText || '—'), margin: [0, 0, 0, 2] },
                        {
                            table: {
                                body: [[{ text: 'DO NOT DISPENSE', bold: true, alignment: 'center', fontSize: 10 }]]
                            },
                            layout: {
                                hLineWidth: () => 0.5,
                                vLineWidth: () => 0.5,
                                hLineColor: () => '#000000',
                                vLineColor: () => '#000000',
                                paddingLeft: () => 4,
                                paddingRight: () => 4,
                                paddingTop: () => 2,
                                paddingBottom: () => 2
                            }
                        }
                    ], margin: [0, 4, 0, 4]
                }
                : (intentText || '—');

            return [
                row.drug || '—',
                row.dose || '—',
                intentCell,
                row.route || '—',
                row.frequency || '—',
                row.duration || '—',
                row.quantity || '—'
            ];
        });
    }

    private buildMedicationTableRow(context: MedicationRequestContext): MedicationTableRow {
        const request = context.medicationRequest;
        const dosage = Array.isArray(request?.dosageInstruction) ? request?.dosageInstruction[0] : null;
        const dispenseQuantity = request?.dispenseRequest?.quantity as Quantity | undefined;
        const fallbackQuantity = context.dispenses?.[0]?.quantity as Quantity | undefined;
        return {
            drug: this.buildDrugDisplay(context),
            // Keep formatDose focused on dosing values (do not include dosageInstruction.text here)
            dose: this.formatDose(dosage),
            route: this.codeableConceptToText(dosage?.route) || '',
            frequency: this.formatFrequency(dosage),
            duration: this.formatDurationValue(
                (dosage?.timing?.repeat?.duration as any),
                (dosage?.timing?.repeat?.durationUnit as any)
            ),
            quantity: this.formatQuantity(dispenseQuantity) || this.formatQuantity(fallbackQuantity)
        };
    }

    private buildDrugDisplay(context: MedicationRequestContext): string {
        const name = this.getMedicationName(context) || 'Medication';
        const ingredientSummary = this.buildMedicationIngredientSummary(context);
        return ingredientSummary ? `${name}\n${ingredientSummary}` : name;
    }

    private buildMedicationIngredientSummary(context: MedicationRequestContext): string {
        const medication = context.medication || this.extractContainedMedication(context.medicationRequest);
        if (!medication) {
            return '';
        }
        const summary = this.utility.extractMedicationIngredients(medication, {
            includePrefix: true,
            prefix: 'Ingredients',
            delimiter: ' • ',
            maxItems: 4,
            onlyActive: false
        });
        if (!summary) {
            return '';
        }
        return `(${summary})`;
    }

    private extractContainedMedication(request?: MedicationRequest | null): Medication | null {
        if (!request?.contained?.length) {
            return null;
        }
        for (const resource of request.contained) {
            if (resource && (resource as any).resourceType === 'Medication') {
                return resource as Medication;
            }
        }
        return null;
    }

    private getMedicationName(context: MedicationRequestContext | null | undefined): string {
        if (!context?.medicationRequest) {
            return '';
        }
        const request = context.medicationRequest as any;
        const medicationFromCode = this.codeableConceptToText(request?.medicationCodeableConcept);
        if (medicationFromCode) {
            return medicationFromCode;
        }
        const medicationFromResource = context.medication ? this.codeableConceptToText(context.medication.code) : '';
        if (medicationFromResource) {
            return medicationFromResource;
        }
        return request?.medicationReference?.display || request?.medicationReference?.reference || '';
    }

    private formatDose(dosage?: Dosage | null): string {
        if (!dosage) {
            return '';
        }
        const entries = Array.isArray(dosage.doseAndRate) ? dosage.doseAndRate : [];
        if (entries.length) {
            const entry = entries[0] as any;
            if (entry?.doseQuantity) {
                return this.formatQuantity(entry.doseQuantity as Quantity);
            }
            if (entry?.doseRange?.low || entry?.doseRange?.high) {
                const low = entry?.doseRange?.low ? this.formatQuantity(entry.doseRange.low as Quantity) : '';
                const high = entry?.doseRange?.high ? this.formatQuantity(entry.doseRange.high as Quantity) : '';
                return [low, high].filter(Boolean).join(' - ');
            }
        }
        // Do not fall back to dosage.text for dose display; keep dose focused on quantitative values only.
        return '';
    }

    private formatFrequency(dosage?: Dosage | null): string {
        if (!dosage?.timing) {
            return '';
        }
        const repeat = dosage.timing.repeat;
        const parts: string[] = [];
        if (typeof repeat?.frequency === 'number') {
            parts.push(`${repeat.frequency}x`);
        } else if (typeof repeat?.frequencyMax === 'number') {
            parts.push(`${repeat.frequencyMax}x`);
        }
        if (repeat?.period) {
            const unit = repeat.periodUnit ? this.normalizePeriodUnit(repeat.periodUnit) : '';
            parts.push(`per ${repeat.period} ${unit}`.trim());
        } else if (repeat?.periodUnit) {
            parts.push(`per ${this.normalizePeriodUnit(repeat.periodUnit)}`);
        }
        if (repeat?.when?.length) {
            parts.push(`(${repeat.when.join(', ')})`);
        }
        if (parts.length) {
            return parts.join(' ');
        }
        if (dosage.timing.code) {
            return this.codeableConceptToText(dosage.timing.code);
        }
        return '';
    }

    private normalizePeriodUnit(unit: string): string {
        const map: Record<string, string> = {
            a: 'year',
            wk: 'week',
            mo: 'month',
            d: 'day',
            h: 'hour',
            min: 'minute',
            s: 'second'
        };
        const normalized = (unit || '').toLowerCase();
        return this.titleCase(map[normalized] || unit || '');
    }

    private formatDurationValue(duration?: any, durationUnit?: string): string {
        if (!duration) {
            return '';
        }
        const value = duration ?? '';
        const unit = durationUnit || '';
        if (typeof value === 'number' && value >= 0) {
            const unitText = unit ? this.titleCase(unit) : '';
            return `${value} ${this.normalizePeriodUnit(unitText)}`.trim();
        }
        // if (duration.text) {
        //     return duration.text;
        // }
        return '';
    }

    private formatQuantity(quantity?: Quantity | null): string {
        if (!quantity) {
            return '';
        }
        const value = typeof quantity.value === 'number' ? quantity.value : quantity.value ?? '';
        const unit = quantity.unit || quantity.code || '';
        if (value === '' && !unit) {
            return '';
        }
        return `${value}${unit ? ` ${unit}` : ''}`.trim();
    }

    private gatherPatientInstructions(contexts: MedicationRequestContext[]): string {
        const lines: string[] = [];

        contexts.forEach((ctx) => {
            const req = ctx.medicationRequest;
            const drugName = this.getMedicationName(ctx) || 'Medication';
            const drugInstructions: string[] = [];

            const dosageList = Array.isArray(req?.dosageInstruction) ? req.dosageInstruction : [];
            dosageList.forEach((dosage) => {
                if (dosage.patientInstruction) {
                    drugInstructions.push(dosage.patientInstruction.trim());
                }
                if (Array.isArray(dosage.additionalInstruction) && dosage.additionalInstruction.length) {
                    const text = this.codeableConceptArrayToText(dosage.additionalInstruction);
                    if (text) {
                        drugInstructions.push(text);
                    }
                }
                if (dosage.text) {
                    drugInstructions.push(dosage.text);
                }
            });

            const notes = Array.isArray(req?.note) ? req.note : [];
            notes.forEach((note) => {
                if (note?.text) {
                    drugInstructions.push(note.text);
                }
            });

            if (drugInstructions.length) {
                // Deduplicate instructions for this specific drug
                const uniqueInstructions = Array.from(new Set(drugInstructions));
                lines.push(`${drugName}: ${uniqueInstructions.join('; ')}`);
            }
        });

        if (!lines.length) {
            return 'No patient instructions documented.';
        }
        return lines.join('\n');
    }

    private buildDispenseSectionText(requests: MedicationRequest[]): string {
        if (!requests.length) {
            return 'N/A';
        }
        const substitution = this.describeSubstitution(requests);
        const refills = this.describeRefills(requests);
        const performerLines = Array.from(new Set(
            requests
                .map((req) => (req.dispenseRequest as any)?.performer?.display || (req.dispenseRequest as any)?.performer?.reference)
                .filter((entry): entry is string => !!entry)
        ));
        const lines = [substitution, refills];
        if (performerLines.length) {
            lines.push(`Dispense Site: ${performerLines.join(', ')}`);
        }
        return lines.filter(Boolean).join('\n') || 'N/A';
    }

    private describeSubstitution(requests: MedicationRequest[]): string {
        const statuses = requests.map((req) => {
            const substitution = req?.substitution;
            if (!substitution) {
                return 'unspecified';
            }
            if (typeof substitution.allowedBoolean === 'boolean') {
                return substitution.allowedBoolean ? 'allowed' : 'not allowed';
            }
            const coded = this.codeableConceptToText(substitution.allowedCodeableConcept);
            return coded || 'unspecified';
        });
        const unique = Array.from(new Set(statuses.filter(Boolean)));
        if (!unique.length || unique.every((entry) => entry === 'unspecified')) {
            return 'Generic substitution: Unspecified';
        }
        if (unique.length === 1 && (unique[0] === 'allowed' || unique[0] === 'not allowed')) {
            return `Generic substitution ${unique[0]}`;
        }
        if (unique.length === 1) {
            return `Generic substitution: ${this.titleCase(unique[0])}`;
        }
        return 'Generic substitution: Varies per item';
    }

    private describeRefills(requests: MedicationRequest[]): string {
        const pairs = requests
            .map((req) => ({
                id: req?.id || null,
                value: typeof req?.dispenseRequest?.numberOfRepeatsAllowed === 'number'
                    ? String(req.dispenseRequest?.numberOfRepeatsAllowed)
                    : ''
            }))
            .filter((pair) => !!pair.value);

        if (!pairs.length) {
            return 'Refill: N/A';
        }

        const unique = Array.from(new Set(pairs.map((pair) => pair.value)));
        if (unique.length === 1) {
            return `Refill: ${unique[0]}`;
        }

        const detailed = pairs.map((pair) =>
            pair.id ? `${pair.value} (Rx ${pair.id})` : pair.value
        );
        return `Refill: ${detailed.join(', ')}`;
    }

    private formatAllergies(allergies: AllergyIntolerance[]): string {
        if (!Array.isArray(allergies) || !allergies.length) {
            return 'None documented';
        }
        const labels = allergies
            .map((allergy) => this.codeableConceptToText(allergy.code) || allergy.code?.coding?.[0]?.display || allergy.code?.text || '')
            .filter((label) => label.trim().length > 0);
        return labels.length ? labels.join(', ') : 'Documented (see chart)';
    }

    private buildMedicationDiagnosisText(context: MedicationRequestContext | undefined): string {
        if (!context) {
            return 'N/A';
        }
        return this.buildDiagnosisFromRequest(context.medicationRequest, context.reasonResources);
    }

    private buildCombinedMedicationDiagnosisText(contexts: MedicationRequestContext[]): string {
        const set = new Set<string>();
        contexts.forEach((context) => {
            const diagnosis = this.buildDiagnosisFromRequest(context.medicationRequest, context.reasonResources);
            if (diagnosis && diagnosis !== 'N/A') {
                diagnosis.split(/;\s*/).forEach((entry) => {
                    if (entry?.trim()) {
                        set.add(entry.trim());
                    }
                });
            }
        });
        return set.size ? Array.from(set).join('; ') : 'N/A';
    }

    private buildDiagnosisFromRequest(request: MedicationRequest | null | undefined, reasonResources: Resource[]): string {
        if (!request) {
            return 'N/A';
        }
        const reasonCodes = Array.isArray(request.reasonCode)
            ? request.reasonCode.map((rc) => this.codeableConceptToText(rc)).filter(Boolean)
            : [];
        const references = Array.isArray(request.reasonReference)
            ? request.reasonReference.map((ref: any) => ref?.reference || '').filter(Boolean)
            : [];
        const referenceTexts = references
            .map((ref) => this.formatReasonResource(
                reasonResources.find((resource) => referenceMatches(ref, `${resource.resourceType}/${(resource as any)?.id}`))
            ))
            .filter(Boolean);
        const combined = [...reasonCodes, ...referenceTexts].filter(Boolean).join('; ');
        return combined || 'N/A';
    }

    private formatReasonResource(resource: Resource | undefined): string {
        if (!resource) {
            return '';
        }
        if (resource.resourceType === 'Condition') {
            const condition = resource as Condition;
            return this.codeableConceptToText(condition.code);
        }
        if ((resource as any)?.code) {
            return this.codeableConceptToText((resource as any).code);
        }
        return (resource as any)?.title || (resource as any)?.text || '';
    }

    private summarizeMedicationContexts(contexts: MedicationRequestContext[]): string {
        if (!contexts.length) {
            return 'No medications documented';
        }
        const names = contexts
            .map((context) => this.getMedicationName(context))
            .filter((name) => !!name?.trim());
        if (!names.length) {
            return `${contexts.length} medication request(s)`;
        }
        const unique = Array.from(new Set(names));
        const preview = unique.slice(0, 4).join(', ');
        const extra = unique.length > 4 ? ` +${unique.length - 4} more` : '';
        return `${contexts.length} medication(s): ${preview}${extra}`.trim();
    }

    private buildQrVerificationBlock(value?: string | null) {
        const normalized = (value ?? '').toString().trim();
        if (!normalized) {
            return null;
        }
        return {
            stack: [
                { text: 'QR VERIFICATION', style: 'sectionHeader', fillColor: '#eeeeee', margin: [0, 12, 0, 4] },
                { qr: normalized, fit: 96, alignment: 'center', margin: [0, 4, 0, 4] },
                { text: normalized, style: 'smallValue', alignment: 'center' }
            ]
        };
    }

    private buildResourceMap(entries?: Array<BundleEntry | null | undefined>): Map<string, Resource> {
        const map = new Map<string, Resource>();
        (entries || []).forEach((entry) => {
            const resource = entry?.resource as Resource | undefined;
            if (!resource) {
                return;
            }
            const id = (resource as any)?.id;
            if (resource.resourceType && id) {
                map.set(`${resource.resourceType}/${id}`, resource);
            }
            if (entry?.fullUrl) {
                map.set(entry.fullUrl, resource);
            }
        });
        return map;
    }

    private findResourceByReference<T extends Resource>(map: Map<string, Resource>, reference?: string | null): T | null {
        const ref = (reference || '').toString().trim();
        if (!ref) {
            return null;
        }
        if (map.has(ref)) {
            return map.get(ref) as T;
        }
        for (const [key, resource] of map.entries()) {
            if (referenceMatches(ref, key)) {
                return resource as T;
            }
        }
        return null;
    }

    private resolveOrganizationResource(map: Map<string, Resource>): Organization | null {
        for (const resource of map.values()) {
            if (resource?.resourceType === 'Organization') {
                return resource as Organization;
            }
        }
        return null;
    }

    private extractPerformerReference(sr: ServiceRequest | null): string | null {
        if (!sr) {
            return null;
        }
        const performers = Array.isArray(sr.performer)
            ? sr.performer
            : sr.performer
                ? [sr.performer]
                : [];
        if (!performers.length) {
            return null;
        }
        const ref = (performers[0] as any)?.reference || performers[0];
        return typeof ref === 'string' && ref.trim() ? ref : null;
    }

    private async fetchOrganizationById(id: string | null | undefined): Promise<Organization | null> {
        const normalized = (id || '').toString().trim();
        if (!normalized) {
            return null;
        }
        try {
            return await firstValueFrom(
                this.http.get<Organization>(`${this.backend}/Organization/${normalized}?_format=json`, {
                    context: new HttpContext().set(LoadingUIEnabled, false)
                })
            );
        } catch {
            return null;
        }
    }

    private getOrganizationTenantId(serviceRequest: ServiceRequest | null | undefined): string | null {
        if (!serviceRequest) {
            return null;
        }

        const tags: any[] = Array.isArray(serviceRequest?.meta?.tag) ? serviceRequest.meta.tag : [];
        const tenantTag = tags.find((coding: any) => this.isOrganizationTenantSystem(coding?.system));
        const tenantFromTag = this.normalizeOrganizationId(tenantTag?.code ?? tenantTag?.value);
        if (tenantFromTag) {
            return tenantFromTag;
        }

        const extensions: any[] = Array.isArray(serviceRequest?.extension) ? serviceRequest.extension : [];
        const tenantExtension = extensions.find((ext: any) =>
            this.isOrganizationTenantExtension(ext?.url) || this.isLegacyOrganizationExtension(ext?.url)
        );
        const extensionValue = tenantExtension?.valueReference?.reference
            ?? tenantExtension?.valueString
            ?? tenantExtension?.valueIdentifier?.value
            ?? null;
        const tenantFromExtension = this.normalizeOrganizationId(extensionValue);
        if (tenantFromExtension) {
            return tenantFromExtension;
        }

        const performers: any[] = Array.isArray(serviceRequest?.performer)
            ? serviceRequest.performer as any[]
            : serviceRequest?.performer
                ? [serviceRequest.performer]
                : [];
        for (const performer of performers) {
            const ref = performer?.reference ?? performer;
            const performerOrg = this.normalizeOrganizationId(ref, true);
            if (performerOrg) {
                return performerOrg;
            }
        }

        return null;
    }

    private isOrganizationTenantSystem(system: string | null | undefined): boolean {
        const normalized = (system ?? '').toString().trim().toLowerCase();
        if (!normalized) {
            return false;
        }
        return normalized === this.organizationTenantTagSystem.toLowerCase() || normalized.endsWith('/codesystem/organizationtenant');
    }

    private isOrganizationTenantExtension(url: string | null | undefined): boolean {
        const normalized = (url ?? '').toString().trim().toLowerCase();
        if (!normalized) {
            return false;
        }
        return normalized === this.organizationTenantExtensionUrl.toLowerCase() || normalized.endsWith('/structuredefinition/organizationtenant');
    }

    private isLegacyOrganizationExtension(url: string | null | undefined): boolean {
        const normalized = (url ?? '').toString().trim().toLowerCase();
        if (!normalized) {
            return false;
        }
        return normalized === this.legacyHospitalExtensionUrl.toLowerCase() || normalized === this.actorOrganizationExtensionUrl.toLowerCase();
    }

    private normalizeOrganizationId(raw: unknown, requireOrganizationKeyword = false): string | null {
        const value = (raw ?? '').toString().trim();
        if (!value) {
            return null;
        }

        const sanitized = value.split(/[?#]/)[0];
        const lower = sanitized.toLowerCase();
        const marker = 'organization/';
        const idx = lower.lastIndexOf(marker);
        if (idx >= 0) {
            const remainder = sanitized.substring(idx + marker.length).split('/')[0];
            return remainder || null;
        }

        if (requireOrganizationKeyword) {
            return null;
        }

        return sanitized || null;
    }

    private formatDateTime(value?: string): string {
        if (!value) {
            return '';
        }
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? '' : date.toLocaleString();
    }

    private titleCase(str: string): string {
        if (!str) {
            return '';
        }
        return str
            .split(/\s|-/)
            .filter(Boolean)
            .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase())
            .join(' ');
    }

    private reasonToText(sr: any): string {
        const reasonCodes = Array.isArray(sr?.reasonCode)
            ? sr.reasonCode.map((rc: any) => this.codeableConceptToText(rc)).filter(Boolean)
            : [];
        const reasonRefs = Array.isArray(sr?.reasonReference)
            ? sr.reasonReference.map((ref: any) => ref?.display || ref?.reference).filter(Boolean)
            : [];
        const reasonString = reasonCodes.join('; ');
        const refString = reasonRefs.length ? `References: ${reasonRefs.join(', ')}` : '';
        return [reasonString, refString].filter(Boolean).join('\n');
    }

    private notesToText(sr: any): string {
        const collected = Array.isArray(sr?.note)
            ? sr.note.map((note: any) => note?.text).filter(Boolean)
            : [];
        if (sr?.flattenedNote) {
            collected.push(sr.flattenedNote);
        }
        return collected.join('\n');
    }

    private formatAddress(address?: any): string {
        if (!address) {
            return '';
        }
        const line = Array.isArray(address.line) ? address.line.join(', ') : address.line || '';
        const cityLine = [address.city, address.state, address.postalCode].filter(Boolean).join(', ');
        const parts = [line, cityLine, address.country].filter(Boolean);
        return parts.join(' | ');
    }

    private formatTelecomLines(telecom?: Array<{ system?: string; value?: string }>): string[] {
        if (!Array.isArray(telecom)) {
            return [];
        }

        return telecom
            .map((entry) => {
                if (!entry?.value) {
                    return '';
                }
                return entry.system ? `${this.titleCase(entry.system)}: ${entry.value}` : entry.value;
            })
            .filter((line) => line.trim().length > 0);
    }

    private formatTelecom(telecom?: Array<{ system?: string; value?: string }>): string {
        const items = Array.isArray(telecom)
            ? telecom
                .map((entry) => {
                    if (!entry?.value) {
                        return '';
                    }
                    return entry.system ? `${this.titleCase(entry.system)}: ${entry.value}` : entry.value;
                })
                .filter(Boolean)
            : [];
        return items.join(' | ');
    }

    private computeAge(birthDate?: string): string {
        if (!birthDate) {
            return '';
        }
        const dob = new Date(birthDate);
        if (Number.isNaN(dob.getTime())) {
            return '';
        }
        const today = new Date();
        let age = today.getFullYear() - dob.getFullYear();
        const monthDiff = today.getMonth() - dob.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
            age -= 1;
        }
        return `${age} yrs`;
    }

    private getPatientName(patient?: Patient | null): string {
        if (!patient?.name?.length) {
            return '';
        }
        const preferred = patient.name.find((n) => n.use === 'official') || patient.name[0];
        const given = Array.isArray(preferred?.given) ? preferred.given.join(' ') : preferred?.given || '';
        const family = preferred?.family || '';
        return [given, family].filter(Boolean).join(' ').trim();
    }

    private getPractitionerName(practitioner?: Practitioner | null): string {
        if (!practitioner?.name?.length) {
            return '';
        }
        const preferred = practitioner.name.find((n) => n.use === 'official') || practitioner.name[0];
        const given = Array.isArray(preferred?.given) ? preferred.given.join(' ') : preferred?.given || '';
        const family = preferred?.family || '';
        return [given, family].filter(Boolean).join(' ').trim();
    }

    private extractHospitalIdentifierFromPatient(patient?: Patient | null): string {
        if (!patient?.identifier?.length) {
            return '';
        }

        const hospitalIdentifier = patient.identifier.find((identifier) =>
            identifier?.system === 'https://elikita-server.daalitech.com/fhir/identifier/pid'
        );

        return hospitalIdentifier?.value || '';
    }

    private async resolveOrganizationLogo(org?: Organization | null): Promise<string | null> {
        const source = this.extractOrganizationLogoSource(org);
        if (!source) {
            return null;
        }

        if (source.startsWith('data:')) {
            return source;
        }

        return await this.fetchImageAsDataUrl(source);
    }

    private extractOrganizationLogoSource(org?: Organization | null): string | null {
        if (!org) {
            return null;
        }

        const photo = (org as any)?.photo?.[0];
        if (photo?.data) {
            return this.composeDataUrl(photo.data, photo.contentType);
        }

        const extensions = Array.isArray(org.extension) ? org.extension : [];
        const match = extensions.find((ext: any) => this.isOrganizationLogoExtension(ext?.url));
        if (!match) {
            return null;
        }

        if (match.valueString && match.valueString.startsWith('data:')) {
            return match.valueString;
        }
        if (match.valueUrl) {
            return match.valueUrl;
        }

        const attachment = match.valueAttachment;
        if (attachment?.data) {
            return this.composeDataUrl(attachment.data, attachment.contentType);
        }
        if (attachment?.url) {
            return attachment.url;
        }
        if (attachment?.title && attachment.title.startsWith('data:')) {
            return attachment.title;
        }

        return null;
    }

    private isOrganizationLogoExtension(url?: string | null): boolean {
        const normalized = (url || '').toLowerCase();
        if (!normalized) {
            return false;
        }
        return normalized.endsWith('/structuredefinition/organizationlogo') || normalized.includes('organizationlogo');
    }

    private composeDataUrl(base64Data: string, contentType?: string): string {
        const safeContentType = (contentType || 'image/png').trim() || 'image/png';
        return `data:${safeContentType};base64,${base64Data}`;
    }

    private async fetchImageAsDataUrl(url: string): Promise<string | null> {
        if (typeof fetch !== 'function') {
            return null;
        }
        try {
            const response = await fetch(url);
            if (!response?.ok) {
                return null;
            }
            const blob = await response.blob();
            return await this.blobToDataUrl(blob);
        } catch {
            return null;
        }
    }

    private blobToDataUrl(blob: Blob): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve((reader.result as string) || '');
            reader.onerror = () => reject(new Error('Failed to read image blob.'));
            reader.readAsDataURL(blob);
        });
    }

    private codeableConceptToText(cc: any): string {
        if (!cc) return '';
        if (typeof cc === 'string') return cc;
        const coding = Array.isArray(cc.coding) ? cc.coding : [];
        return cc.text || coding[0]?.display || coding[0]?.code || '';
    }

    private codeableConceptArrayToText(arr: any): string {
        const list = Array.isArray(arr) ? arr : [];
        return list.map((cc) => this.codeableConceptToText(cc)).filter(Boolean).join(', ');
    }

    private dosageToText(dosage: any): string {
        const list = Array.isArray(dosage) ? dosage : (dosage ? [dosage] : []);
        const texts = list
            .map((d: any) => d?.text || '')
            .filter((t: any) => typeof t === 'string' && t.trim().length);
        return texts.join('\n');
    }

    private getBusinessRequestId(sr: any): string {
        if (!sr) return 'N/A';
        return sr.identifier?.find((i: any) => i.system === 'https://elikita-server.daalitech.com/fhir/identifier/sr' || i.value?.startsWith('SR-'))?.value || sr.id || 'N/A';
    }
}
